import { defineMessages } from "../define";

/** Pizza detail: header, photo viewer, evaluation panel, details, annotation, developer diagnostics, verdict. */
export default defineMessages({
  en: {
    // verdict (lib/pizza-verdict: check labels and failure reasons, also shown in the lists)
    "verdict.check.photo": "Photo quality",
    "verdict.check.base": "Ingredients",
    "verdict.check.extras": "Extras",
    "verdict.check.removals": "Removals",
    "verdict.check.burnt": "Burnt",
    "verdict.check.raw": "Uncooked",
    "verdict.check.saver": "Pizza saver",
    "verdict.reason.noPizza": "No pizza found in the photo",
    "verdict.reason.missing": "Missing: {list}",
    "verdict.reason.unexpected": "Not ordered: {list}",
    "verdict.reason.missingExtras.one": "Missing extra: {list}",
    "verdict.reason.missingExtras.few": "Missing extras: {list}",
    "verdict.reason.missingExtras.other": "Missing extras: {list}",
    "verdict.reason.notRemoved": "Not removed: {list}",
    "verdict.reason.burnt": "Burnt crust",
    "verdict.reason.raw": "Uncooked crust",
    "verdict.reason.saverMissing": "Pizza saver missing",
    "verdict.reason.checkFailed": "{check}: NOK",

    // shared by the detail
    "detail.evaluating": "Evaluating…",
    "detail.doneness": "Doneness",
    "detail.pizzaId": "Pizza ID",
    "detail.orderId": "Order ID",
    "detail.copyPizzaId": "Copy pizza ID",
    "detail.copyOrderId": "Copy order ID",

    // header
    "detail.header.reevaluate": "Reevaluate",
    "detail.header.developerMode": "Developer mode",
    "detail.header.developerModeOn": "Developer mode on",
    "detail.header.developerModeOff": "Developer mode off",
    "detail.header.moreActions": "More actions",
    "detail.header.order": "Order {number}",
    "detail.header.captureCount": "{count}× captured",
    "detail.header.closeDetail": "Close the detail",
    "detail.header.backToOrder": "Back to the order",

    // browsing (header arrows, phone bar)
    "detail.nav.inOrder": "in order",
    "detail.nav.prevOrder": "Prev. order",
    "detail.nav.nextOrder": "Next order",

    // photo viewer
    "detail.viewer.layers": "Photo layers",
    "detail.viewer.original": "Original",
    "detail.viewer.masked": "Masked",
    "detail.viewer.showWholeFrame": "Show the whole frame",
    "detail.viewer.cropToPizza": "Crop to the pizza",
    "detail.viewer.wholeFrame": "Whole frame",
    "detail.viewer.noPhoto": "No photo available",

    // developer layers
    "detail.layers.title": "Evaluation layers",
    "detail.layers.maskLayers": "Mask layers",
    "detail.layers.show": "Show {name}",
    "detail.layers.opacity": "{name} opacity",
    "detail.layers.mask": "Mask",
    "detail.layers.blendMode": "Blend mode",
    "detail.layers.blendModes": "Blend modes",
    "detail.layer.source": "Source photo",
    "detail.layer.heatmap": "Doneness heatmap (red = burnt, blue = uncooked)",
    "detail.layer.heatmapBurnt": "Burnt response (source heatmap)",
    "detail.layer.heatmapUncooked": "Uncooked response (source heatmap)",
    "detail.layer.pizzaMask": "Pizza region",
    "detail.layer.crust": "Crust region",
    "detail.layer.detectedComponents": "Detected components",
    "detail.layer.quarter": "Quarter {n}",
    "detail.blend.normal": "Normal",
    "detail.blend.multiply": "Multiply",
    "detail.blend.screen": "Screen",
    "detail.blend.overlay": "Overlay",
    "detail.blend.darken": "Darken",
    "detail.blend.lighten": "Lighten",
    "detail.blend.colorDodge": "Color dodge",
    "detail.blend.colorBurn": "Color burn",
    "detail.blend.hardLight": "Hard light",
    "detail.blend.softLight": "Soft light",
    "detail.blend.difference": "Difference",
    "detail.blend.exclusion": "Exclusion",
    "detail.blend.hue": "Hue",
    "detail.blend.saturation": "Saturation",
    "detail.blend.color": "Color",
    "detail.blend.luminosity": "Luminosity",

    // evaluation panel
    "detail.panel.title": "Evaluation",
    "detail.panel.loading": "Loading the evaluation",
    "detail.panel.allPassed.one": "{count} check passed",
    "detail.panel.allPassed.few": "All {count} checks passed",
    "detail.panel.allPassed.other": "All {count} checks passed",
    "detail.panel.failed.one": "{failed} of {count} check failed",
    "detail.panel.failed.few": "{failed} of {count} checks failed",
    "detail.panel.failed.other": "{failed} of {count} checks failed",
    "detail.panel.noEvaluation": "No evaluation stored for this pizza",
    "detail.panel.showObstacleLayer": "Show the obstacle layer",
    "detail.panel.missingCount": "{count} missing",
    "detail.panel.unexpectedCount": "{count} not ordered",
    "detail.panel.foundCount": "{count} found",
    "detail.panel.notDetectableCount": "{count} not detectable",
    "detail.panel.noIngredients": "No ingredients expected",
    "detail.panel.stillOnList": "Still on: {list}",
    "detail.panel.removedList": "Removed: {list}",
    "detail.panel.burntValue": "Burnt: {value}",
    "detail.panel.rawValue": "Uncooked: {value}",
    "detail.panel.yes": "yes",
    "detail.panel.no": "no",
    "detail.panel.burntAndRaw": "Burnt and uncooked",
    "detail.panel.rawNotEvaluated": "uncooked not evaluated",
    "detail.panel.saverMissing": "Missing",
    "detail.panel.saverPresent": "Present",
    "detail.panel.saverWaived": "Not required – pizza on a plate",
    "detail.panel.plateShare": "Plate share",
    "detail.panel.plateRule": "Plate rule {state}",
    "detail.panel.plateRuleOn": "on",
    "detail.panel.plateRuleOff": "off",
    "detail.panel.saverDetected": "pizza saver detected: {value}",
    "detail.panel.model": "Model",
    "detail.panel.classification": "Classification",
    "detail.panel.confidence": "Confidence",
    "detail.panel.components": "Components",

    // ingredient chips (evaluation panel)
    "detail.chip.detected": "Detected",
    "detail.chip.missing": "Missing",
    "detail.chip.unexpected": "Not ordered",
    "detail.chip.notDetectable": "Not detectable",
    "detail.chip.detectedTitle": "Detected on the pizza",
    "detail.chip.missingTitle": "Expected, but not found on the pizza",
    "detail.chip.unexpectedTitle": "On the pizza, but neither the recipe nor the order has it",
    "detail.chip.notDetectableTitle": "Not detectable by the model",
    "detail.chip.stillOn": "still on",
    "detail.chip.removed": "removed",
    "detail.chip.stillOnTitle": "Still on the pizza, should have been removed",
    "detail.chip.removedTitle": "Not on the pizza, removed as ordered",
    "detail.extra.pieceTitle": "Neither the recipe nor the order has it; the detector found {count} confident pieces",
    "detail.extra.sauceTitle": "Neither the recipe nor the order has it; the sauce covers {share} of the pizza",
    "detail.extra.ignored": "Look-alikes never counted on this pizza: {list}",
    "detail.extra.rule": "Not ordered from {count} pieces with confidence ≥ {confidence}",
    "detail.extra.off": "Check for ingredients beyond the order: off",
    "detail.chip.legendStillOn": "Still on the pizza",
    "detail.chip.legendRemoved": "Removed",

    // photo-quality checks
    "detail.photo.check.pizza_blur": "Blur",
    "detail.photo.check.obstacle_in_pizza": "Obstacles",
    "detail.photo.check.pizza_visibility": "Visibility",
    "detail.photo.skipped": "Skipped: {reason}",
    "detail.photo.blurShadow": "Shadow mode: with the check enforced, the photo would be invalid",
    "detail.photo.segmentationSkipped": "Segmentation skipped: {reason}",
    "detail.photo.coveredBy": "Covered by: {list}",
    "detail.photo.segmentationShadow": "Shadow mode: with the segmentation enforced, the photo would be invalid",
    "detail.photo.crustGap": "Crust gap {value}",
    "detail.photo.crustGapLimit": "Crust gap {value} (limit {limit})",
    "detail.photo.preview": "Preview computed now with the obstacle model. The stored check is unchanged.",
    "detail.photo.previewWouldBe":
      "Preview computed now with the obstacle model: the check would be {status}. The stored check is unchanged.",

    // doneness (gauges, burnt model, legacy chart)
    "detail.doneness.noData": "No doneness data",
    "detail.gauge.threshold": "threshold {value}",
    "detail.gauge.vsThreshold": "{value} vs. threshold {threshold}",
    "detail.burntModel.pBurnt": "p(burnt)",
    "detail.burntModel.pRaw": "p(uncooked)",
    "detail.burntModel.borderline": "borderline",
    "detail.burntModel.moderate": "moderate",
    "detail.burntModel.confident": "confident",
    "detail.burntModel.certainty": "{certainty} · threshold {threshold}",
    "detail.burntModel.tta": "Test-time augmentation",
    "detail.burntModel.ttaValue": "{count}× (shift/flip averaged)",
    "detail.burntModel.inference": "Inference",
    "detail.burntModel.crustInStrip": "Crust in the strip",
    "detail.burntModel.lowCrust": "low – check the crust mask",
    "detail.burntModel.polarStrip": "Polar strip",
    "detail.burntModel.explainTwoOutputs":
      "Two independent outputs of one network: a crust can be burnt and uncooked at once. The heatmap layer is a diverging map of the two Grad-CAM responses (each scaled by its probability): yellow/red = burnt, cyan/blue = uncooked, green = both alike; the source heatmaps of the two outputs are separate layers. A confident OK pizza has an empty heatmap.",
    "detail.burntModel.explainOneOutput":
      "The heatmap layer shows where the network responded (Grad-CAM scaled by p(burnt), red/yellow); a confident OK pizza has an empty heatmap. This model does not evaluate uncooked crust.",
    "detail.donenessChart.raw": "Raw",
    "detail.donenessChart.cooked": "Cooked",
    "detail.donenessChart.burnt": "Burnt",
    "detail.donenessChart.charred": "Charred",

    // details card
    "detail.facts.type": "Type",
    "detail.facts.captures": "Captures",
    "detail.facts.captured": "Captured",

    // order strip
    "detail.strip.label": "Pizzas of the order",
    "detail.strip.notCaptured": "Not captured",

    // annotation
    "annotation.title": "Annotation",
    "annotation.hotkeysShort": "B / R / C, then O / N",
    "annotation.hotkeysHelp":
      "Press B (burnt), R (raw) or C (components) to pick a row, then O for OK or N for NOK. Esc cancels.",
    "annotation.category.burnt": "Burnt",
    "annotation.category.raw": "Raw",
    "annotation.category.components": "Components",
    "annotation.match": "The annotation matches the evaluation",
    "annotation.mismatch": "The annotation does not match the evaluation",
    "annotation.missing": "Not annotated yet",
    "annotation.noEvaluation": "No evaluation to compare with",

    // developer diagnostics
    "diag.title": "Diagnostics",
    "diag.sectionsLabel": "Diagnostics sections",
    "diag.section.findings": "Findings",
    "diag.section.timing": "Timing",
    "diag.section.record": "Pizza",
    "diag.section.detection": "Detection",
    "diag.section.photo": "Photo quality",
    "diag.section.components": "Components",
    "diag.section.sauce": "Sauces",
    "diag.section.doneness": "Doneness",
    "diag.section.saver": "Pizza saver",
    "diag.section.slicing": "Slicing",
    "diag.section.layers": "Layers",
    "diag.section.raw": "Raw data",
    "diag.findingCount.one": "{count} finding",
    "diag.findingCount.few": "{count} findings",
    "diag.findingCount.other": "{count} findings",
    "diag.copyJson": "Copy as JSON: {what}",
    "diag.allFields": "All fields",
    "diag.legacy": "{name} (legacy)",
    "diag.findings.none": "No findings: the stored evaluation is consistent.",
    "diag.timing.title": "Pipeline timing",
    "diag.timing.afterCapture": "({delay} after the capture)",
    "diag.timing.none": "No processing times stored",
    "diag.record.title": "Pizza record",
    "diag.record.pizzaType": "pizza type",
    "diag.record.otherFields": "All other fields",
    "diag.detection.title": "Detection and geometry",
    "diag.detection.aspect": "aspect {value}",
    "diag.photo.none": "No photo-quality evaluation",
    "diag.components.recipe": "recipe",
    "diag.components.none": "No component evaluation",
    "diag.components.hoverHint": "hover a row to see it on the photo",
    "diag.components.noDetections": "No detections",
    "diag.components.detectedSame": "same as components_bbs",
    "diag.components.detectedDiffers": "differs, see below",
    "diag.sauce.title": "Sauce map",
    "diag.sauce.noShares": "No sauce found on the pizza (no shares)",
    "diag.sauce.none": "No sauce map stored",
    "diag.doneness.allFields": "{name} (all fields)",
    "diag.doneness.none": "No doneness evaluation",
    "diag.saver.none": "No pizza-saver evaluation",
    "diag.saver.noDetection": "No pizza-saver detection in components_bbs",
    "diag.slicing.none": "No slicing stored",
    "diag.layers.title": "Layers and assets",
    "diag.other.title": "Other metadata",
    "diag.raw.metadata": "Metadata",
    "diag.raw.pizzaDocument": "Pizza document",
    "diag.raw.response": "Evaluation response",
    "diag.raw.search.one": "Search {count} field by path or value…",
    "diag.raw.search.few": "Search {count} fields by path or value…",
    "diag.raw.search.other": "Search {count} fields by path or value…",
    "diag.raw.noMatch": "No field matches",
    "diag.raw.metadataFields.one": "{count} metadata field",
    "diag.raw.metadataFields.few": "{count} metadata fields",
    "diag.raw.metadataFields.other": "{count} metadata fields",
    "diag.raw.noMetadata": "No metadata",
    "diag.delay.days.one": "{count} day",
    "diag.delay.days.few": "{count} days",
    "diag.delay.days.other": "{count} days",

    // diagnostics findings (diagnostics/findings.ts)
    "diag.part.ingredients": "ingredients",
    "diag.part.removals": "removals",
    "diag.part.extras": "extras",
    "diag.part.burnt": "burnt",
    "diag.part.raw": "raw",
    "diag.part.saver": "pizza saver",
    "diag.part.photo": "photo quality",
    "diag.finding.noMetadata": "No evaluation metadata",
    "diag.finding.noMetadataDetail": "The pizza has no stored evaluation (or the evaluate request failed).",
    "diag.finding.overallMismatch": "Overall {status} does not match its checks ({recomputed})",
    "diag.finding.overallMissingParts": "Missing parts count as failed: {parts}.",
    "diag.finding.overallDisagrees": "The stored overall status disagrees with the stored sub-checks.",
    "diag.finding.lacksParts": "The evaluation lacks {parts}",
    "diag.finding.lacksPartsDetail": "A missing part makes the overall verdict NOK.",
    "diag.finding.noRoi": "No pizza ROI stored for a found pizza",
    "diag.finding.presenceTier": "Pizza presence tier “{tier}”",
    "diag.finding.presenceTierDetail": "Only part of the crust ring was found; the crust mask may be incomplete.",
    "diag.finding.blurSkipped": "Blur check skipped",
    "diag.finding.blurShadow": "Blur check would fail (shadow mode)",
    "diag.finding.blurShadowDetail": "p(blurred) {p} ≥ threshold {threshold}, not enforced.",
    "diag.finding.blurBorderline": "Borderline blur decision",
    "diag.finding.blurBorderlineDetail": "p(blurred) {p} vs. threshold {threshold}.",
    "diag.finding.obstacleNoSegmentation": "Obstacle check without segmentation",
    "diag.finding.obstacleNoSegmentationDetail": "Evaluated before the obstacle model: only the crust gap decided.",
    "diag.finding.obstacleSkipped": "Obstacle segmentation skipped",
    "diag.finding.obstacleShadow": "Obstacle segmentation would fail (shadow mode)",
    "diag.finding.obstacleShadowDetail": "Coverage {coverage} ≥ {threshold}, not enforced.",
    "diag.finding.frameEdge": "Pizza crosses the frame edge ({value} of R)",
    "diag.finding.frameEdgeDetail": "Limit {value}.",
    "diag.finding.burntMismatch": "Burnt verdict does not match its probability",
    "diag.finding.rawMismatch": "Uncooked verdict does not match its probability",
    "diag.finding.mismatchDetail": "p {p} vs. threshold {threshold}, stored {status}.",
    "diag.finding.burntBorderline": "Borderline burnt decision",
    "diag.finding.rawBorderline": "Borderline uncooked decision",
    "diag.finding.borderlineDetail": "p {p} vs. threshold {threshold}.",
    "diag.finding.noProvenance": "No doneness model provenance stored",
    "diag.finding.lowCrust": "Little crust in the polar strip ({value})",
    "diag.finding.lowCrustDetail": "The doneness model saw little crust: check the crust mask and the circle fit.",
    "diag.finding.noDetectable": "No detectable ingredient on this pizza",
    "diag.finding.noDetectableDetail":
      "None of the expected ingredients is detectable: the ingredient check cannot fail.",
    "diag.finding.lowConfidence.one": "{count} detection below 50% confidence",
    "diag.finding.lowConfidence.few": "{count} detections below 50% confidence",
    "diag.finding.lowConfidence.other": "{count} detections below 50% confidence",
    "diag.finding.slicingSkipped": "Slicing skipped",
    "diag.finding.missingLayers.one": "Missing layer: {names}",
    "diag.finding.missingLayers.few": "Missing layers: {names}",
    "diag.finding.missingLayers.other": "Missing layers: {names}",
    "diag.finding.slowTotal": "Slow evaluation: {value} s",
    "diag.finding.slowStep": "Slow step {step}: {value} s",
    "diag.finding.lateEvaluation": "Evaluated {delay} after the capture",
    "diag.finding.lateEvaluationDetail":
      "A re-evaluation or a backfill: the stored result may come from a newer pipeline.",
    "diag.finding.recaptured": "Captured {count}×",
    "diag.finding.recapturedDetail": "The photo was retaken; the evaluation is of the last capture.",
  },
  cs: {
    // verdict (lib/pizza-verdict: check labels and failure reasons, also shown in the lists)
    "verdict.check.photo": "Kvalita fotky",
    "verdict.check.base": "Suroviny",
    "verdict.check.extras": "Přídavky",
    "verdict.check.removals": "Odebrané suroviny",
    "verdict.check.burnt": "Spálená",
    "verdict.check.raw": "Nedopečená",
    "verdict.check.saver": "Pizza saver",
    "verdict.reason.noPizza": "Na fotce není pizza",
    "verdict.reason.missing": "Chybí: {list}",
    "verdict.reason.unexpected": "Navíc: {list}",
    "verdict.reason.missingExtras.one": "Chybí přídavek: {list}",
    "verdict.reason.missingExtras.few": "Chybí přídavky: {list}",
    "verdict.reason.missingExtras.other": "Chybí přídavky: {list}",
    "verdict.reason.notRemoved": "Neodebráno: {list}",
    "verdict.reason.burnt": "Spálený okraj",
    "verdict.reason.raw": "Nedopečený okraj",
    "verdict.reason.saverMissing": "Chybí pizza saver",
    "verdict.reason.checkFailed": "{check}: NEOK",

    // shared by the detail
    "detail.evaluating": "Vyhodnocuji…",
    "detail.doneness": "Propečení",
    "detail.pizzaId": "ID pizzy",
    "detail.orderId": "ID objednávky",
    "detail.copyPizzaId": "Kopírovat ID pizzy",
    "detail.copyOrderId": "Kopírovat ID objednávky",

    // header
    "detail.header.reevaluate": "Znovu vyhodnotit",
    "detail.header.developerMode": "Vývojářský režim",
    "detail.header.developerModeOn": "Vývojářský režim je zapnutý",
    "detail.header.developerModeOff": "Vývojářský režim je vypnutý",
    "detail.header.moreActions": "Další akce",
    "detail.header.order": "Objednávka {number}",
    "detail.header.captureCount": "pořízeno {count}×",
    "detail.header.closeDetail": "Zavřít detail",
    "detail.header.backToOrder": "Zpět na objednávku",

    // browsing (header arrows, phone bar)
    "detail.nav.inOrder": "v objednávce",
    "detail.nav.prevOrder": "Předchozí obj.",
    "detail.nav.nextOrder": "Další obj.",

    // photo viewer
    "detail.viewer.layers": "Vrstvy fotky",
    "detail.viewer.original": "Originál",
    "detail.viewer.masked": "Maskovaná",
    "detail.viewer.showWholeFrame": "Zobrazit celý snímek",
    "detail.viewer.cropToPizza": "Oříznout na pizzu",
    "detail.viewer.wholeFrame": "Celý snímek",
    "detail.viewer.noPhoto": "Fotka není k dispozici",

    // developer layers
    "detail.layers.title": "Vrstvy vyhodnocení",
    "detail.layers.maskLayers": "Vrstvy masek",
    "detail.layers.show": "Zobrazit {name}",
    "detail.layers.opacity": "Krytí vrstvy {name}",
    "detail.layers.mask": "Maska",
    "detail.layers.blendMode": "Režim prolnutí",
    "detail.layers.blendModes": "Režimy prolnutí",
    "detail.layer.source": "Původní fotka",
    "detail.layer.heatmap": "Heatmapa propečení (červená = spálená, modrá = nedopečená)",
    "detail.layer.heatmapBurnt": "Odezva spálení (zdrojová heatmapa)",
    "detail.layer.heatmapUncooked": "Odezva nedopečení (zdrojová heatmapa)",
    "detail.layer.pizzaMask": "Oblast pizzy",
    "detail.layer.crust": "Oblast okraje",
    "detail.layer.detectedComponents": "Detekované komponenty",
    "detail.layer.quarter": "{n}. čtvrtina",
    "detail.blend.normal": "Normální",
    "detail.blend.multiply": "Násobit",
    "detail.blend.screen": "Závoj",
    "detail.blend.overlay": "Překrýt",
    "detail.blend.darken": "Ztmavit",
    "detail.blend.lighten": "Zesvětlit",
    "detail.blend.colorDodge": "Zesvětlit barvy",
    "detail.blend.colorBurn": "Ztmavit barvy",
    "detail.blend.hardLight": "Tvrdé světlo",
    "detail.blend.softLight": "Měkké světlo",
    "detail.blend.difference": "Rozdíl",
    "detail.blend.exclusion": "Vyloučit",
    "detail.blend.hue": "Odstín",
    "detail.blend.saturation": "Sytost",
    "detail.blend.color": "Barva",
    "detail.blend.luminosity": "Světlost",

    // evaluation panel
    "detail.panel.title": "Vyhodnocení",
    "detail.panel.loading": "Načítám vyhodnocení",
    "detail.panel.allPassed.one": "{count} kontrola prošla",
    "detail.panel.allPassed.few": "Všechny {count} kontroly prošly",
    "detail.panel.allPassed.other": "Všech {count} kontrol prošlo",
    "detail.panel.failed.one": "Neprošlé kontroly: {failed} z {count}",
    "detail.panel.failed.few": "Neprošlé kontroly: {failed} z {count}",
    "detail.panel.failed.other": "Neprošlé kontroly: {failed} z {count}",
    "detail.panel.noEvaluation": "Pizza nemá uložené vyhodnocení",
    "detail.panel.showObstacleLayer": "Zobrazit vrstvu překážek",
    "detail.panel.missingCount": "chybí: {count}",
    "detail.panel.unexpectedCount": "navíc: {count}",
    "detail.panel.foundCount": "nalezeno: {count}",
    "detail.panel.notDetectableCount": "nedetekovatelné: {count}",
    "detail.panel.noIngredients": "Žádné suroviny se neočekávají",
    "detail.panel.stillOnList": "Zůstalo na pizze: {list}",
    "detail.panel.removedList": "Odebráno: {list}",
    "detail.panel.burntValue": "Spálená: {value}",
    "detail.panel.rawValue": "Nedopečená: {value}",
    "detail.panel.yes": "ano",
    "detail.panel.no": "ne",
    "detail.panel.burntAndRaw": "Spálená i nedopečená",
    "detail.panel.rawNotEvaluated": "nedopečení se nevyhodnocuje",
    "detail.panel.saverMissing": "Chybí",
    "detail.panel.saverPresent": "Je na pizze",
    "detail.panel.saverWaived": "Není potřeba – pizza je na talíři",
    "detail.panel.plateShare": "Podíl talíře",
    "detail.panel.plateRule": "Pravidlo talíře {state}",
    "detail.panel.plateRuleOn": "zapnuto",
    "detail.panel.plateRuleOff": "vypnuto",
    "detail.panel.saverDetected": "pizza saver nalezen: {value}",
    "detail.panel.model": "Model",
    "detail.panel.classification": "Klasifikace",
    "detail.panel.confidence": "Jistota",
    "detail.panel.components": "Komponenty",

    // ingredient chips (evaluation panel)
    "detail.chip.detected": "Detekováno",
    "detail.chip.missing": "Chybí",
    "detail.chip.unexpected": "Navíc",
    "detail.chip.notDetectable": "Nedetekovatelné",
    "detail.chip.detectedTitle": "Detekováno na pizze",
    "detail.chip.missingTitle": "Očekáváno, ale na pizze nenalezeno",
    "detail.chip.unexpectedTitle": "Na pizze je, ale nemá ji recept ani objednávka",
    "detail.chip.notDetectableTitle": "Model tuto surovinu nedokáže detekovat",
    "detail.chip.stillOn": "zůstala na pizze",
    "detail.chip.removed": "odebráno",
    "detail.chip.stillOnTitle": "Zůstala na pizze, měla být odebrána",
    "detail.chip.removedTitle": "Na pizze není, odebráno podle objednávky",
    "detail.extra.pieceTitle": "Nemá ji recept ani objednávka; detektor našel {count} jistých kusů",
    "detail.extra.sauceTitle": "Nemá ji recept ani objednávka; omáčka pokrývá {share} pizzy",
    "detail.extra.ignored": "Podobné suroviny, které se u této pizzy nikdy nepočítají: {list}",
    "detail.extra.rule": "Navíc od {count} kusů s jistotou ≥ {confidence}",
    "detail.extra.off": "Kontrola surovin navíc je vypnutá",
    "detail.chip.legendStillOn": "Zůstala na pizze",
    "detail.chip.legendRemoved": "Odebráno",

    // photo-quality checks
    "detail.photo.check.pizza_blur": "Rozmazání",
    "detail.photo.check.obstacle_in_pizza": "Překážky",
    "detail.photo.check.pizza_visibility": "Viditelnost",
    "detail.photo.skipped": "Přeskočeno: {reason}",
    "detail.photo.blurShadow": "Stínový režim: při vynucené kontrole by fotka byla neplatná",
    "detail.photo.segmentationSkipped": "Segmentace přeskočena: {reason}",
    "detail.photo.coveredBy": "Zakrývá: {list}",
    "detail.photo.segmentationShadow": "Stínový režim: při vynucené segmentaci by fotka byla neplatná",
    "detail.photo.crustGap": "Mezera v okraji {value}",
    "detail.photo.crustGapLimit": "Mezera v okraji {value} (limit {limit})",
    "detail.photo.preview": "Náhled spočítaný teď modelem překážek. Uložená kontrola se nemění.",
    "detail.photo.previewWouldBe":
      "Náhled spočítaný teď modelem překážek: kontrola by byla {status}. Uložená kontrola se nemění.",

    // doneness (gauges, burnt model, legacy chart)
    "detail.doneness.noData": "Žádná data o propečení",
    "detail.gauge.threshold": "práh {value}",
    "detail.gauge.vsThreshold": "{value} vs. práh {threshold}",
    "detail.burntModel.pBurnt": "p(spálená)",
    "detail.burntModel.pRaw": "p(nedopečená)",
    "detail.burntModel.borderline": "hraniční",
    "detail.burntModel.moderate": "středně jisté",
    "detail.burntModel.confident": "jisté",
    "detail.burntModel.certainty": "{certainty} · práh {threshold}",
    "detail.burntModel.tta": "Augmentace při inferenci (TTA)",
    "detail.burntModel.ttaValue": "{count}× (průměr posunů a zrcadlení)",
    "detail.burntModel.inference": "Inference",
    "detail.burntModel.crustInStrip": "Okraj v pásu",
    "detail.burntModel.lowCrust": "málo – zkontrolujte masku okraje",
    "detail.burntModel.polarStrip": "Polární pás",
    "detail.burntModel.explainTwoOutputs":
      "Dva nezávislé výstupy jedné sítě: okraj může být zároveň spálený i nedopečený. Vrstva heatmapy je divergentní mapa obou odezev Grad-CAM (každá vážená svou pravděpodobností): žlutá/červená = spálená, azurová/modrá = nedopečená, zelená = obojí stejně; zdrojové heatmapy obou výstupů jsou samostatné vrstvy. Jistě OK pizza má prázdnou heatmapu.",
    "detail.burntModel.explainOneOutput":
      "Vrstva heatmapy ukazuje, kde síť reagovala (Grad-CAM vážený p(burnt), červená/žlutá); jistě OK pizza má prázdnou heatmapu. Nedopečení tento model nevyhodnocuje.",
    "detail.donenessChart.raw": "Nedopečená",
    "detail.donenessChart.cooked": "Propečená",
    "detail.donenessChart.burnt": "Spálená",
    "detail.donenessChart.charred": "Zuhelnatělá",

    // details card
    "detail.facts.type": "Typ",
    "detail.facts.captures": "Počet pořízení",
    "detail.facts.captured": "Pořízeno",

    // order strip
    "detail.strip.label": "Pizzy objednávky",
    "detail.strip.notCaptured": "Nepořízeno",

    // annotation
    "annotation.title": "Anotace",
    "annotation.hotkeysShort": "B / R / C, pak O / N",
    "annotation.hotkeysHelp":
      "Klávesou B (spálená), R (nedopečená) nebo C (suroviny) vyberte řádek, pak O pro OK nebo N pro NEOK. Esc výběr zruší.",
    "annotation.category.burnt": "Spálená",
    "annotation.category.raw": "Nedopečená",
    "annotation.category.components": "Suroviny",
    "annotation.match": "Anotace se shoduje s vyhodnocením",
    "annotation.mismatch": "Anotace se neshoduje s vyhodnocením",
    "annotation.missing": "Zatím bez anotace",
    "annotation.noEvaluation": "Chybí vyhodnocení k porovnání",

    // developer diagnostics
    "diag.title": "Diagnostika",
    "diag.sectionsLabel": "Sekce diagnostiky",
    "diag.section.findings": "Nálezy",
    "diag.section.timing": "Časy",
    "diag.section.record": "Pizza",
    "diag.section.detection": "Detekce",
    "diag.section.photo": "Kvalita fotky",
    "diag.section.components": "Komponenty",
    "diag.section.sauce": "Omáčky",
    "diag.section.doneness": "Propečení",
    "diag.section.saver": "Pizza saver",
    "diag.section.slicing": "Krájení",
    "diag.section.layers": "Vrstvy",
    "diag.section.raw": "Surová data",
    "diag.findingCount.one": "{count} nález",
    "diag.findingCount.few": "{count} nálezy",
    "diag.findingCount.other": "{count} nálezů",
    "diag.copyJson": "Kopírovat jako JSON: {what}",
    "diag.allFields": "Všechna pole",
    "diag.legacy": "{name} (starší formát)",
    "diag.findings.none": "Žádné nálezy: uložené vyhodnocení je konzistentní.",
    "diag.timing.title": "Časy pipeline",
    "diag.timing.afterCapture": "({delay} po pořízení)",
    "diag.timing.none": "Časy zpracování nejsou uložené",
    "diag.record.title": "Záznam pizzy",
    "diag.record.pizzaType": "typ pizzy",
    "diag.record.otherFields": "Všechna ostatní pole",
    "diag.detection.title": "Detekce a geometrie",
    "diag.detection.aspect": "poměr stran {value}",
    "diag.photo.none": "Kvalita fotky není vyhodnocená",
    "diag.components.recipe": "receptura",
    "diag.components.none": "Komponenty nejsou vyhodnocené",
    "diag.components.hoverHint": "najetím na řádek ho zobrazíte na fotce",
    "diag.components.noDetections": "Žádné detekce",
    "diag.components.detectedSame": "stejné jako components_bbs",
    "diag.components.detectedDiffers": "liší se, viz níže",
    "diag.sauce.title": "Mapa omáček",
    "diag.sauce.noShares": "Na pizze není žádná omáčka (žádné podíly)",
    "diag.sauce.none": "Mapa omáček není uložená",
    "diag.doneness.allFields": "{name} (všechna pole)",
    "diag.doneness.none": "Propečení není vyhodnocené",
    "diag.saver.none": "Pizza saver není vyhodnocený",
    "diag.saver.noDetection": "V components_bbs není žádná detekce pizza saveru",
    "diag.slicing.none": "Krájení není uložené",
    "diag.layers.title": "Vrstvy a soubory",
    "diag.other.title": "Ostatní metadata",
    "diag.raw.metadata": "Metadata",
    "diag.raw.pizzaDocument": "Dokument pizzy",
    "diag.raw.response": "Odpověď vyhodnocení",
    "diag.raw.search.one": "Hledat v {count} poli podle cesty nebo hodnoty…",
    "diag.raw.search.few": "Hledat v {count} polích podle cesty nebo hodnoty…",
    "diag.raw.search.other": "Hledat v {count} polích podle cesty nebo hodnoty…",
    "diag.raw.noMatch": "Žádné pole neodpovídá",
    "diag.raw.metadataFields.one": "{count} pole metadat",
    "diag.raw.metadataFields.few": "{count} pole metadat",
    "diag.raw.metadataFields.other": "{count} polí metadat",
    "diag.raw.noMetadata": "Žádná metadata",
    "diag.delay.days.one": "{count} den",
    "diag.delay.days.few": "{count} dny",
    "diag.delay.days.other": "{count} dní",

    // diagnostics findings (diagnostics/findings.ts)
    "diag.part.ingredients": "suroviny",
    "diag.part.removals": "odebrané suroviny",
    "diag.part.extras": "přídavky",
    "diag.part.burnt": "spálení",
    "diag.part.raw": "nedopečení",
    "diag.part.saver": "pizza saver",
    "diag.part.photo": "kvalita fotky",
    "diag.finding.noMetadata": "Chybí metadata vyhodnocení",
    "diag.finding.noMetadataDetail": "Pizza nemá uložené vyhodnocení (nebo požadavek na vyhodnocení selhal).",
    "diag.finding.overallMismatch": "Celkový stav {status} neodpovídá kontrolám ({recomputed})",
    "diag.finding.overallMissingParts": "Chybějící části se počítají jako neúspěšné: {parts}.",
    "diag.finding.overallDisagrees": "Uložený celkový stav nesouhlasí s uloženými dílčími kontrolami.",
    "diag.finding.lacksParts": "Ve vyhodnocení chybí: {parts}",
    "diag.finding.lacksPartsDetail": "Kvůli chybějící části je celkový verdikt NEOK.",
    "diag.finding.noRoi": "Nalezená pizza nemá uložené ROI",
    "diag.finding.presenceTier": "Úroveň detekce pizzy „{tier}“",
    "diag.finding.presenceTierDetail": "Nalezena jen část prstence okraje; maska okraje může být neúplná.",
    "diag.finding.blurSkipped": "Kontrola rozmazání přeskočena",
    "diag.finding.blurShadow": "Kontrola rozmazání by neprošla (stínový režim)",
    "diag.finding.blurShadowDetail": "p(blurred) {p} ≥ práh {threshold}, nevynucuje se.",
    "diag.finding.blurBorderline": "Hraniční rozhodnutí o rozmazání",
    "diag.finding.blurBorderlineDetail": "p(blurred) {p} vs. práh {threshold}.",
    "diag.finding.obstacleNoSegmentation": "Kontrola překážek bez segmentace",
    "diag.finding.obstacleNoSegmentationDetail":
      "Vyhodnoceno před nasazením modelu překážek: rozhodovala jen mezera v okraji.",
    "diag.finding.obstacleSkipped": "Segmentace překážek přeskočena",
    "diag.finding.obstacleShadow": "Segmentace překážek by neprošla (stínový režim)",
    "diag.finding.obstacleShadowDetail": "Zakrytí {coverage} ≥ {threshold}, nevynucuje se.",
    "diag.finding.frameEdge": "Pizza přesahuje okraj snímku ({value} z R)",
    "diag.finding.frameEdgeDetail": "Limit {value}.",
    "diag.finding.burntMismatch": "Verdikt spálení neodpovídá pravděpodobnosti",
    "diag.finding.rawMismatch": "Verdikt nedopečení neodpovídá pravděpodobnosti",
    "diag.finding.mismatchDetail": "p {p} vs. práh {threshold}, uloženo {status}.",
    "diag.finding.burntBorderline": "Hraniční rozhodnutí o spálení",
    "diag.finding.rawBorderline": "Hraniční rozhodnutí o nedopečení",
    "diag.finding.borderlineDetail": "p {p} vs. práh {threshold}.",
    "diag.finding.noProvenance": "Chybí údaje o modelu propečení",
    "diag.finding.lowCrust": "Málo okraje v polárním pásu ({value})",
    "diag.finding.lowCrustDetail": "Model propečení viděl málo okraje: zkontrolujte masku okraje a proložení kružnice.",
    "diag.finding.noDetectable": "Pizza nemá žádnou detekovatelnou surovinu",
    "diag.finding.noDetectableDetail":
      "Žádnou z očekávaných surovin model nedokáže detekovat: kontrola surovin nemůže neprojít.",
    "diag.finding.lowConfidence.one": "{count} detekce s jistotou pod 50 %",
    "diag.finding.lowConfidence.few": "{count} detekce s jistotou pod 50 %",
    "diag.finding.lowConfidence.other": "{count} detekcí s jistotou pod 50 %",
    "diag.finding.slicingSkipped": "Krájení přeskočeno",
    "diag.finding.missingLayers.one": "Chybí vrstva: {names}",
    "diag.finding.missingLayers.few": "Chybí vrstvy: {names}",
    "diag.finding.missingLayers.other": "Chybí vrstvy: {names}",
    "diag.finding.slowTotal": "Pomalé vyhodnocení: {value} s",
    "diag.finding.slowStep": "Pomalý krok {step}: {value} s",
    "diag.finding.lateEvaluation": "Vyhodnoceno {delay} po pořízení",
    "diag.finding.lateEvaluationDetail":
      "Nové vyhodnocení nebo dodatečné doplnění: uložený výsledek může pocházet z novější pipeline.",
    "diag.finding.recaptured": "Pořízeno {count}×",
    "diag.finding.recapturedDetail": "Fotka byla pořízena znovu; vyhodnocení se týká posledního pořízení.",
  },
});
